import Phaser from 'phaser';
import type { HeroClass } from '@roe/shared';
import { GAME_W, GAME_H, setupCamera } from '../dpr.js';
import { GameStateManager } from '../state/GameStateManager';
import { HERO_OPTIONS } from './HeroSelectScene';


interface ShopItem {
  heroClass: HeroClass;
  heroKey?: string;
  label: string;
  description: string;
  goldCost: number;
  runeCost: number;
  spriteKey: string;
}

// Derived from HERO_OPTIONS so the armory always matches the hero select screen.
// Hidden heroes (WIP / unreleased — e.g. Zhorak) are filtered out of the recruit list.
const SHOP_ITEMS: ShopItem[] = HERO_OPTIONS.filter((opt) => !opt.hidden).map((opt) => ({
  heroClass: opt.heroClass,
  heroKey: opt.heroKey,
  label: opt.name,
  description: opt.description,
  goldCost: 0,
  runeCost: 0,
  spriteKey: `hero_${opt.heroKey ?? opt.heroClass}`,
}));

export class ShopScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private battleScene: (Phaser.Scene & { resumeBattle(): void }) | null = null;
  private fromPauseMenu = false;

  constructor() {
    super({ key: 'ShopScene' });
  }

  init(data: { gsm?: GameStateManager; battleScene?: any; fromPauseMenu?: boolean }): void {
    this.gsm = data.gsm || GameStateManager.getInstance();
    this.battleScene = data.battleScene || null;
    this.fromPauseMenu = data.fromPauseMenu ?? false;
  }

  create(): void {
    if (this.battleScene) {
      setupCamera(this, this.cameras.main, 'overlay');
      this.cameras.main.setBackgroundColor('rgba(5, 3, 5, 0.97)');
    } else {
      setupCamera(this, this.cameras.main, 'full');
      this.cameras.main.setBackgroundColor('#0a0608');
    }
    if (!this.gsm) this.gsm = GameStateManager.getInstance();

    const height = GAME_H;
    const W = this.battleScene ? 480 : GAME_W;

    // ── Header ──
    this.add.rectangle(W / 2, 22, W, 44, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(W / 2, 44, W, 1, 0x5a3600, 0.9).setDepth(10);

    this.add.text(W / 2, 14, '✦  The Armory  ✦', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Hero slot info
    const maxSlots = this.gsm.getMaxDeploySlots();
    const deployed = this.gsm.player.deployedHeroes.length;
    const slotColor = deployed < maxSlots ? '#6aaa60' : '#c8973a';
    const nextSlotTxt = maxSlots >= 3 ? 'Max roster reached' : `next slot at Stage ${maxSlots * 5}`;
    this.add.text(W / 2, 32, `Slots: ${deployed}/${maxSlots}  ·  ${nextSlotTxt}`, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: slotColor,
    }).setOrigin(0.5).setDepth(11);

    // Close button
    const closeBtn = this.add.text(W - 20, 14, '✕', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(11);
    const closeShop = () => {
      if (this.fromPauseMenu) {
        this.scene.stop();
        this.scene.launch('MenuScene', { gsm: this.gsm, battleScene: this.battleScene });
      } else if (this.battleScene) {
        (this.battleScene as any).resumeBattle();
        this.scene.stop();
      } else {
        this.scene.start('MenuScene');
      }
    };
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', closeShop);

    // ESC to close — mirror the ✕ button
    this.input.keyboard?.once('keydown-ESC', closeShop);

    this.drawShopGrid(W, height);
  }

  private drawShopGrid(width: number, height: number): void {
    const cols = 2;
    const cardW = 226;
    const cardH = 130;
    const gapY = 8;
    const HEADER_H = 48;
    const BOTTOM_PAD = 12;
    const SCROLL_TOP = HEADER_H;
    const SCROLL_H = height - SCROLL_TOP - BOTTOM_PAD;
    const startX = (width - cols * cardW) / 2 + cardW / 2;
    const rowCount = Math.ceil(SHOP_ITEMS.length / cols);
    const CONTENT_H = rowCount * (cardH + gapY) + 8;
    let scrollY = 0;
    const maxScroll = Math.max(0, CONTENT_H - SCROLL_H);

    // Bottom border strip
    this.add.rectangle(width / 2, height - BOTTOM_PAD / 2, width, BOTTOM_PAD, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(width / 2, height - BOTTOM_PAD, width, 1, 0x5a3600, 0.9).setDepth(10);

    // Scrollable container
    const scrollCont = this.add.container(0, SCROLL_TOP);

    // Clip mask
    const maskGfx = this.make.graphics({});
    maskGfx.fillRect(0, SCROLL_TOP, width, SCROLL_H);
    scrollCont.setMask(maskGfx.createGeometryMask());

    // Scroll bar
    let scrollThumb: Phaser.GameObjects.Rectangle | null = null;
    if (maxScroll > 0) {
      const trackX = width - 5;
      this.add.rectangle(trackX, SCROLL_TOP + SCROLL_H / 2, 4, SCROLL_H, 0x3a2800, 0.5).setDepth(12);
      const thumbH = Math.max(20, (SCROLL_H / CONTENT_H) * SCROLL_H);
      scrollThumb = this.add.rectangle(trackX, SCROLL_TOP, 4, thumbH, 0x5a3600, 0.8)
        .setOrigin(0.5, 0).setDepth(12);
    }

    const applyScroll = (dy: number) => {
      scrollY = Phaser.Math.Clamp(scrollY + dy, 0, maxScroll);
      scrollCont.y = SCROLL_TOP - scrollY;
      if (scrollThumb && maxScroll > 0) {
        const thumbRange = SCROLL_H - scrollThumb.height;
        scrollThumb.y = SCROLL_TOP + (scrollY / maxScroll) * thumbRange;
      }
    };

    this.input.on('wheel', (_ptr: Phaser.Input.Pointer, _gos: unknown, _dx: unknown, dy: number) => {
      applyScroll((dy as number) * 0.5);
    });
    let dragStartY = 0;
    let dragScrollStart = 0;
    let isDragging = false;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y < SCROLL_TOP) return;
      dragStartY = pointer.y; dragScrollStart = scrollY; isDragging = false;
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      const dy = dragStartY - pointer.y;
      if (Math.abs(dy) > 4) isDragging = true;
      if (isDragging) applyScroll(dy - (scrollY - dragScrollStart));
    });

    SHOP_ITEMS.forEach((item, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = startX + col * cardW;
      const cy = 4 + row * (cardH + gapY) + cardH / 2;

      const itemKey = item.heroKey ?? item.heroClass;
      const alreadyOwned = this.gsm.player.heroes.some((h) => (h.heroKey ?? h.heroClass) === itemKey);
      const MAX_RECRUIT = 3;
      const recruitSlots = this.gsm.getMaxDeploySlots(); // stage-gated: 1 / 2 / 3
      const currentRoster = this.gsm.player.heroes.length; // counts dead heroes too
      const atCapacity = !alreadyOwned && currentRoster >= MAX_RECRUIT;
      const slotLocked = !alreadyOwned && !atCapacity && currentRoster >= recruitSlots;
      const canRecruit = !alreadyOwned && !atCapacity && !slotLocked;

      const borderColor = alreadyOwned ? 0x3a6830 : canRecruit ? 0x7a5010 : 0x2a1a08;
      const borderWidth = canRecruit ? 2 : 1;
      const cardBg = this.add.rectangle(cx, cy, cardW - 6, cardH - 2, 0x0d0a08, 0.97)
        .setStrokeStyle(borderWidth, borderColor);
      scrollCont.add(cardBg);

      // Hero portrait
      const portrait = this.add.sprite(cx - 82, cy - 10, item.spriteKey).setDisplaySize(76, 76);
      scrollCont.add(portrait);

      // Name — 12px bold renders ~15 px tall from its top-left origin, so the
      // description must start >= 15 px below the name's Y to avoid visual
      // overlap. Previous layout at cy-36 was only 10 px of spacing.
      const nameTxt = this.add.text(cx - 36, cy - 46, item.label, {
        fontSize: '12px', fontFamily: 'Georgia, serif',
        color: alreadyOwned ? '#6aaa60' : canRecruit ? '#c8973a' : '#5a3600',
        fontStyle: 'bold',
      });
      scrollCont.add(nameTxt);

      // Description. Wrap width sits inside the right border (cardW/2 ≈ 110 px right of
      // center; text origin is cx-36 so the text spans cx-36..cx+100 with a 140 px wrap).
      // 18 px below the name top clears the bold 12 px name with room to spare.
      const descTxt = this.add.text(cx - 36, cy - 28, item.description, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7b5030',
        wordWrap: { width: 140 }, maxLines: 4,
      });
      scrollCont.add(descTxt);

      // Status badge — sits below the description, above the recruit button.
      if (alreadyOwned || atCapacity || slotLocked) {
        const nextStage = recruitSlots * 5; // 5 or 10
        const statusText = alreadyOwned ? '✓ In Roster'
          : atCapacity ? `Full (${currentRoster}/${MAX_RECRUIT})`
          : `Unlocks at Stage ${nextStage}`;
        const statusTxt = this.add.text(cx - 36, cy + 14, statusText, {
          fontSize: '9px', fontFamily: 'Georgia, serif',
          color: alreadyOwned ? '#5a9a50' : slotLocked ? '#7a6030' : '#a04010', fontStyle: 'italic',
        });
        scrollCont.add(statusTxt);
      }

      // Recruit button
      const btnW = canRecruit ? 108 : 80;
      const btnH = canRecruit ? 26 : 18;
      const btnBg = this.add.rectangle(cx + 24, cy + 40, btnW, btnH,
        canRecruit ? 0x0c1c0c : 0x0d0a08, 1)
        .setStrokeStyle(canRecruit ? 2 : 1, canRecruit ? 0x4a8a30 : 0x2a1a08)
        .setInteractive({ useHandCursor: canRecruit });
      scrollCont.add(btnBg);
      const nextStageLabel = recruitSlots < 3 ? `Stage ${recruitSlots * 5} ⟳` : `Full (${currentRoster}/${MAX_RECRUIT})`;
      const btnLabel = alreadyOwned ? 'In Roster' : atCapacity ? `Full (${currentRoster}/${MAX_RECRUIT})` : slotLocked ? nextStageLabel : '⚔ Recruit';
      const btnTxt = this.add.text(cx + 24, cy + 40, btnLabel, {
        fontSize: canRecruit ? '12px' : '9px', fontFamily: 'Georgia, serif',
        color: canRecruit ? '#7acc60' : '#3a2800', fontStyle: canRecruit ? 'bold' : 'normal',
      }).setOrigin(0.5).setInteractive({ useHandCursor: canRecruit });
      scrollCont.add(btnTxt);

      if (canRecruit) {
        btnBg.on('pointerover', () => { btnTxt.setColor('#f0c840'); btnBg.setStrokeStyle(2, 0xb07820); cardBg.setStrokeStyle(2, 0xc8973a); });
        btnBg.on('pointerout', () => { btnTxt.setColor('#7acc60'); btnBg.setStrokeStyle(2, 0x4a8a30); cardBg.setStrokeStyle(borderWidth, borderColor); });
        btnTxt.on('pointerover', () => { btnTxt.setColor('#f0c840'); btnBg.setStrokeStyle(2, 0xb07820); cardBg.setStrokeStyle(2, 0xc8973a); });
        btnTxt.on('pointerout', () => { btnTxt.setColor('#7acc60'); btnBg.setStrokeStyle(2, 0x4a8a30); cardBg.setStrokeStyle(borderWidth, borderColor); });
        const doRecruit = () => {
          this.showConfirm(`Recruit ${item.label}?`, () => {
            const result = this.gsm.purchaseHero(item.heroClass, item.label, item.heroKey);
            if (result === 'ok') {
              const newHeroIdx = this.gsm.player.heroes.length - 1;
              const wasDeployed = this.gsm.player.infernoRunActive ? false : this.gsm.deployHero(newHeroIdx);
              this.gsm.save();
              this.showMessage(wasDeployed ? `${item.label} recruited & deployed!` : `${item.label} recruited!`);
              if (this.battleScene) {
                this.time.delayedCall(800, () => {
                  this.battleScene!.resumeBattle();
                  this.scene.stop();
                });
              } else {
                this.time.delayedCall(1500, () => this.scene.restart());
              }
            } else {
              this.showMessage('Failed.');
            }
          });
        };
        btnBg.on('pointerdown', doRecruit);
        btnTxt.on('pointerdown', doRecruit);
      }
    });
  }

  private drawSellSection(width: number): void {
    const heroes = this.gsm.player.heroes;
    if (heroes.length <= 1) return;

    const startY = 470;
    this.add.text(width / 2, startY, '✦  Dismiss Hero  ✦', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8150f', fontStyle: 'bold',
    }).setOrigin(0.5);

    heroes.forEach((hero, idx) => {
      if (idx === 0) return;
      const y = startY + 22 + (idx - 1) * 24;
      const sellValue = hero.heroClass === 'warrior' || hero.heroClass === 'archer' || hero.heroClass === 'wizard' ? 250 : 500;

      const sbg = this.add.rectangle(width / 2, y + 8, 280, 18, 0x1a0608, 1)
        .setStrokeStyle(1, 0x6b1500).setInteractive({ useHandCursor: true });
      const stxt = this.add.text(width / 2, y + 8,
        `${hero.name} Lv${hero.level} — 🪙${sellValue}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      sbg.on('pointerover', () => { stxt.setColor('#e8a030'); sbg.setStrokeStyle(1, 0xc84000); });
      sbg.on('pointerout', () => { stxt.setColor('#a07840'); sbg.setStrokeStyle(1, 0x6b1500); });
      const doSell = () => {
        this.showConfirm(`Dismiss ${hero.name} for 🪙${sellValue}?`, () => {
          this.gsm.player.heroes.splice(idx, 1);
          this.gsm.player.deployedHeroes = this.gsm.player.deployedHeroes
            .filter((i) => i !== idx)
            .map((i) => (i > idx ? i - 1 : i));
          this.gsm.player.resources.gold += sellValue;
          this.gsm.save();
          this.showMessage(`Dismissed for 🪙${sellValue}`);
          this.time.delayedCall(1200, () => this.scene.restart());
        });
      };
      sbg.on('pointerdown', doSell);
      stxt.on('pointerdown', doSell);
    });
  }

  private showConfirm(message: string, onYes: () => void): void {
    const W = this.battleScene ? 480 : GAME_W;
    const H = this.battleScene ? 576 : GAME_H;
    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.6).setDepth(200).setInteractive();
    const bg = this.add.rectangle(W / 2, H / 2, 320, 120, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0x5a3600).setDepth(201);
    const inner = this.add.rectangle(W / 2, H / 2, 314, 114, 0x0d0a08, 0)
      .setStrokeStyle(1, 0xc8973a, 0.2).setDepth(201);
    const msg = this.add.text(W / 2, H / 2 - 22, message, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840', align: 'center',
      wordWrap: { width: 290 },
    }).setOrigin(0.5).setDepth(202);

    const yesBg = this.add.rectangle(W / 2 - 60, H / 2 + 28, 68, 24, 0x0e1a0e, 1)
      .setStrokeStyle(1, 0x3a7a30).setDepth(202).setInteractive({ useHandCursor: true });
    const yesBtn = this.add.text(W / 2 - 60, H / 2 + 28, 'YES', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#6aaa60', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
    const noBg = this.add.rectangle(W / 2 + 60, H / 2 + 28, 68, 24, 0x1a0608, 1)
      .setStrokeStyle(1, 0x8b2000).setDepth(202).setInteractive({ useHandCursor: true });
    const noBtn = this.add.text(W / 2 + 60, H / 2 + 28, 'NO', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });

    const cleanup = () => [overlay, bg, inner, msg, yesBg, yesBtn, noBg, noBtn].forEach((o) => o.destroy());
    yesBg.on('pointerdown', () => { cleanup(); onYes(); });
    yesBtn.on('pointerdown', () => { cleanup(); onYes(); });
    noBg.on('pointerdown', () => cleanup());
    noBtn.on('pointerdown', () => cleanup());
  }

  private showMessage(text: string): void {
    const W = this.battleScene ? 480 : GAME_W;
    const H = this.battleScene ? 576 : GAME_H;
    const msg = this.add.text(W / 2, H - 30, text, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a',
      backgroundColor: '#0d0a0e', padding: { x: 10, y: 5 },
      stroke: '#5a3600', strokeThickness: 1,
    }).setOrigin(0.5).setDepth(100);
    this.tweens.add({ targets: msg, alpha: 0, delay: 2000, duration: 500, onComplete: () => msg.destroy() });
  }
}
